import { Tab, Tabs } from "@mui/material";
import { endOfDay, sub } from "date-fns";
import { debounce } from "lodash";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { DOCS, FEATURE_FLAGS } from "../../utils/constants";

import { get, getFeatureFlag, isAdmin } from "../../utils/io";
import { useMeasureRealtime } from "../hooks";
import MainContentContainer from "../Layouts/MainContentContainer";
import MeasureList from "../MeasureList";
import TabPanel from "../Users/TabPanel";
import MeasureSearchForm from "./MeasureSearchForm";

const getOneMonthAgo = () => sub(new Date(), { months: 1 });

const getEndOfDay = () => endOfDay(new Date());
const emptySearchInputs = {
  macAddress: "",
  hubId: "",
  status: "",
  takenAfterDate: getOneMonthAgo(),
  takenBeforeDate: getEndOfDay(),
  strictPairHub: false,
};

function Measures() {
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMeasures, setSelectedMeasures] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [searchInputs, setSearchInputs] = useState({ ...emptySearchInputs });
  const [autoRefreshMeasure, setAutoRefreshMeasure] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);

  const pendingStatusesRef = useRef({});
  const searchInputsRef = useRef(searchInputs);

  useEffect(() => {
    searchInputsRef.current = searchInputs;
  }, [searchInputs]);

  const handleRealtimeMessage = useCallback((message) => {
    const currentHubFilter = searchInputsRef.current?.hubId;

    if (message.event === "delivery_status_updated") {
      if (
        currentHubFilter &&
        message.hub_id &&
        message.hub_id !== currentHubFilter
      ) {
        return;
      }

      setSearchResults((prev) => {
        const index = prev.findIndex((m) => m.id === message.measurement_id);
        if (index === -1) {
          // Measure not yet in list! Store it in pending.
          pendingStatusesRef.current[message.measurement_id] =
            message.delivery_status;
          return prev;
        }
        // Found it, update it. Provide a new row reference so DataGrid re-renders
        const next = [...prev];
        next[index] = {
          ...next[index],
          delivery_status: message.delivery_status,
        };
        return next;
      });
    } else if (message.full_measure?.id) {
      const measureHubId =
        message.full_measure?.hub?.hub_id ||
        message.full_measure?.transmitting_hub?.hub_id;
      if (
        currentHubFilter &&
        measureHubId &&
        measureHubId !== currentHubFilter
      ) {
        return;
      }

      const measureId = message.full_measure.id;
      const finalStatus =
        pendingStatusesRef.current[measureId] ||
        message.full_measure.delivery_status;
      const newMeasure = {
        ...message.full_measure,
        delivery_status: finalStatus,
      };

      // Clean up ref to avoid memory leak
      if (pendingStatusesRef.current[measureId]) {
        delete pendingStatusesRef.current[measureId];
      }

      setTotalCount((prev) => prev + 1);
      setSearchResults((prev) => [newMeasure, ...prev]);
    }
  }, []);

  useMeasureRealtime(handleRealtimeMessage);

  const tabs = [
    { index: 0, label: "All" },
    { index: 1, label: "Pending Validation" },
  ];

  const hasRealtimeProtocol = getFeatureFlag(FEATURE_FLAGS.REALTIME_PROTOCOL);
  const showAutoRefresh = isAdmin() || !hasRealtimeProtocol;

  const getMeasureList = useCallback(
    async (searchInputs, paginationModel, sortField) => {
      const res = await get("/measures", {
        esn: searchInputs.hubId,
        device_mac: searchInputs.macAddress,
        status: tabIndex ? "pending clinical validation" : searchInputs.status,
        taken_after: searchInputs.takenAfterDate.toISOString(),
        taken_before: searchInputs.takenBeforeDate.toISOString(),
        hub_filter_option: searchInputs.strictPairHub
          ? "only_pair_hub"
          : "pair_hub_or_transmit_hub",
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
        ordering: sortField,
      });
      if (res?.status === 200) {
        setTotalCount(res.data.total_count);
        setSearchResults(res.data.measures);
      } else {
        return enqueueSnackbar("Unable to retrieve measurements", {
          variant: "error",
        });
      }
      return null;
    },
    [tabIndex]
  );

  const onSelectionChanged = (selectionModel) => {
    setSelectedMeasures(selectionModel);
  };

  const refreshMeasureList = () => setPaginationModel({ ...paginationModel });

  const handleTabChange = (_, newValue) => setTabIndex(newValue);

  const clinicalValidationToolbarProps = {
    visibleToolbarItems: {
      resend: false,
      bindDevices: false,
      unbindDevices: false,
      blockDevices: false,
      export: false,
      autoRefresh: showAutoRefresh,
      approveMeasures: true,
      rejectMeasures: true,
    },
  };

  const updateMeasureGrid = useCallback(() => {
    const debouncedUpdate = debounce(async () => {
      setLoading(true);
      const sortField = sortModel[0]?.field;
      const sortDirection = sortModel[0]?.sort === "desc" ? "-" : "";
      const sortParam = sortField ? `${sortDirection}${sortField}` : undefined;

      await getMeasureList(searchInputs, paginationModel, sortParam);
      setLoading(false);
    }, 300);

    debouncedUpdate();
    return debouncedUpdate.cancel;
  }, [getMeasureList, paginationModel, searchInputs, sortModel, setLoading]);

  useEffect(() => {
    updateMeasureGrid();
  }, [updateMeasureGrid]);

  useEffect(() => {
    let interval;
    if (autoRefreshMeasure) {
      interval = setInterval(updateMeasureGrid, 10000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [autoRefreshMeasure, updateMeasureGrid]);

  const commonProps = {
    checkbox: true,
    loading,
    rows: searchResults,
    onSelectionChanged,
    paginationModel,
    setPaginationModel,
    rowsPerPageOptions: [20, 25, 50, 100],
    rowCount: totalCount,
    refreshMeasureList,
    selectedMeasures,
    setSortModel,
    sortingMode: "server",
    setAutoRefreshMeasure,
    autoRefreshMeasure,
  };

  return (
    <MainContentContainer title="Measures" docsUrl={DOCS.MEASURES.url}>
      {getFeatureFlag(FEATURE_FLAGS.CLINICAL_VALIDATION) && (
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          sx={{ marginBottom: "15px" }}
        >
          {tabs.map((tab) => (
            <Tab key={tab.index} label={tab.label} />
          ))}
        </Tabs>
      )}
      <MeasureSearchForm
        onSubmit={setSearchInputs}
        isSearchDisabled={loading}
        statusPendingValidation={
          tabIndex ? "pending clinical validation" : null
        }
        defaultSearchInputs={emptySearchInputs}
        searchCriterias={{
          hubId: true,
          macAddress: true,
          status: true,
          strictPairHub: true,
        }}
      />
      <TabPanel value={tabIndex} index={0}>
        <MeasureList
          {...commonProps}
          searchResults={searchResults}
          searchInputs={searchInputs}
          toolbarProps={{
            visibleToolbarItems: {
              resend: true,
              bindDevices: true,
              unbindDevices: true,
              blockDevices: true,
              export: true,
              autoRefresh: showAutoRefresh,
            },
          }}
        />
      </TabPanel>
      <TabPanel value={tabIndex} index={1}>
        <MeasureList
          {...commonProps}
          toolbarProps={clinicalValidationToolbarProps}
        />
      </TabPanel>
    </MainContentContainer>
  );
}

export default Measures;
